import React, { useState, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GlobeIcon, BuildingIcon, CalendarIcon, ClockIcon, DollarSignIcon, UserIcon, MailIcon, PhoneIcon, MapPinIcon, DownloadIcon } from 'lucide-react';
import { FormSection, TextAreaField, FileUploadField, DisplayField } from '../components/FormComponents';
import { mockPublishedTenders, mockSuppliers } from '../data/mockData';
export function EOISubmission() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const tender = mockPublishedTenders.find(t => t.id === id);
  // Simulated logged-in supplier
  const supplier = mockSuppliers[0];
  const [formData, setFormData] = useState({
    description: ''
  });
  if (!tender) {
    return <div className="max-w-4xl mx-auto py-12 text-center" data-id="element-785">
        <GlobeIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-786" />
        <h2 className="text-lg font-medium text-gray-900" data-id="element-787">Tender not found</h2>
        <p className="text-sm text-gray-500 mt-1" data-id="element-788">
          The tender you are looking for does not exist.
        </p>
        <button onClick={() => navigate('/tender-board')} className="mt-4 text-indigo-600 hover:text-indigo-500 text-sm font-medium" data-id="element-789">
          ← Back to Tender Board
        </button>
      </div>;
  }
  const handleSubmit = () => {
    if (!formData.description.trim()) {
      alert('Please provide a description for your EOI submission.');
      return;
    }
    alert(`EOI submitted successfully for ${tender.refNo}!`);
    navigate('/tender-board');
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Goods':
        return 'bg-purple-100 text-purple-800';
      case 'Service':
        return 'bg-indigo-100 text-indigo-800';
      case 'Works':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };
  return <div className="space-y-6 max-w-4xl mx-auto" data-id="element-790">
      {/* Header */}
      <div className="flex items-center gap-3" data-id="element-791">
        <div className="p-3 bg-blue-100 rounded-lg" data-id="element-792">
          <GlobeIcon className="h-6 w-6 text-blue-600" data-id="element-793" />
        </div>
        <div data-id="element-794">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-795">EOI Submission</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-796">
            Submit your Expression of Interest for{' '}
            <span className="font-medium text-gray-700" data-id="element-797">{tender.refNo}</span>
          </p>
        </div>
      </div>

      {/* Tender Details Card */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6" data-id="element-798">
        <div className="flex items-center justify-between mb-4" data-id="element-799">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-800">
            Tender Details
          </h2>
          <div className="flex items-center gap-2" data-id="element-801">
            <button type="button" onClick={() => alert(`Downloading EOI document for ${tender.refNo}...`)} className="inline-flex items-center px-3 py-1.5 border border-indigo-300 shadow-sm text-xs font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100" data-id="element-802">
              <DownloadIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-803" />
              Download EOI
            </button>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getCategoryColor(tender.category)}`} data-id="element-804">
              {tender.category}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800" data-id="element-805">
              {tender.status}
            </span>
          </div>
        </div>

        <h3 className="text-base font-semibold text-gray-900 mb-2" data-id="element-806">
          {tender.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-807">{tender.description}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-808">
          <div className="flex items-start gap-2" data-id="element-809">
            <BuildingIcon className="h-4 w-4 text-slate-400 mt-0.5" data-id="element-810" />
            <div data-id="element-811">
              <p className="text-xs text-slate-500" data-id="element-812">Department</p>
              <p className="text-sm font-medium text-gray-900" data-id="element-813">
                {tender.department}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2" data-id="element-814">
            <CalendarIcon className="h-4 w-4 text-slate-400 mt-0.5" data-id="element-815" />
            <div data-id="element-816">
              <p className="text-xs text-slate-500" data-id="element-817">Published</p>
              <p className="text-sm font-medium text-gray-900" data-id="element-818">
                {tender.publishDate}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2" data-id="element-819">
            <ClockIcon className="h-4 w-4 text-indigo-400 mt-0.5" data-id="element-820" />
            <div data-id="element-821">
              <p className="text-xs text-slate-500" data-id="element-822">Deadline</p>
              <p className="text-sm font-medium text-indigo-700" data-id="element-823">
                {tender.submissionDeadline}
              </p>
            </div>
          </div>
          {tender.estimatedBudget && <div className="flex items-start gap-2" data-id="element-824">
              <DollarSignIcon className="h-4 w-4 text-slate-400 mt-0.5" data-id="element-825" />
              <div data-id="element-826">
                <p className="text-xs text-slate-500" data-id="element-827">Est. Budget</p>
                <p className="text-sm font-medium text-gray-900" data-id="element-828">
                  ${tender.estimatedBudget.toLocaleString()}
                </p>
              </div>
            </div>}
        </div>
      </div>

      {/* Supplier Details Card */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6" data-id="element-829">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-830">
          Supplier Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-831">
          <div className="flex items-center gap-3" data-id="element-832">
            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-833">
              <BuildingIcon className="h-5 w-5 text-indigo-600" data-id="element-834" />
            </div>
            <div data-id="element-835">
              <p className="text-sm font-medium text-gray-900" data-id="element-836">
                {supplier.name}
              </p>
              <p className="text-xs text-gray-500" data-id="element-837">{supplier.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-838">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-839">
              <UserIcon className="h-5 w-5 text-gray-600" data-id="element-840" />
            </div>
            <div data-id="element-841">
              <p className="text-sm font-medium text-gray-900" data-id="element-842">
                {supplier.contactPersonName}
              </p>
              <p className="text-xs text-gray-500" data-id="element-843">Contact Person</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-844">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-845">
              <MailIcon className="h-5 w-5 text-gray-600" data-id="element-846" />
            </div>
            <div data-id="element-847">
              <p className="text-sm font-medium text-gray-900" data-id="element-848">
                {supplier.email}
              </p>
              <p className="text-xs text-gray-500" data-id="element-849">Email</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-850">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-851">
              <PhoneIcon className="h-5 w-5 text-gray-600" data-id="element-852" />
            </div>
            <div data-id="element-853">
              <p className="text-sm font-medium text-gray-900" data-id="element-854">
                {supplier.mobile}
              </p>
              <p className="text-xs text-gray-500" data-id="element-855">Phone</p>
            </div>
          </div>
          <div className="flex items-center gap-3 md:col-span-2" data-id="element-856">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-857">
              <MapPinIcon className="h-5 w-5 text-gray-600" data-id="element-858" />
            </div>
            <div data-id="element-859">
              <p className="text-sm font-medium text-gray-900" data-id="element-860">
                {supplier.communicationAddress}
              </p>
              <p className="text-xs text-gray-500" data-id="element-861">Address</p>
            </div>
          </div>
        </div>
      </div>

      {/* Submission Form */}
      <form className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-862">
        <FormSection title="EOI Submission" data-id="element-863">
          <TextAreaField label="Description" value={formData.description} onChange={e => setFormData({
          ...formData,
          description: e.target.value
        })} rows={5} placeholder="Describe your company's qualifications, experience, and interest in this tender..." required data-id="element-864" />
          <FileUploadField label="Supporting Documents" required data-id="element-865" />
        </FormSection>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-866">
          <button type="button" onClick={() => navigate('/tender-board')} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-867">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-868">
            Submit EOI
          </button>
        </div>
      </form>
    </div>;
}